/**
 * MediaPipe 21 Hand Landmark Gesture Recognition Engine
 * Supports Peace Sign (✌️ for Add Point / Action) and Fully Open Palm (🖐️ for Undo Last Point).
 * 
 * Landmark Indices:
 * 0: Wrist
 * 1-4: Thumb (4: Tip, 3: IP, 2: MCP, 1: CMC)
 * 5-8: Index (8: Tip, 7: DIP, 6: PIP, 5: MCP)
 * 9-12: Middle (12: Tip, 11: DIP, 10: PIP, 9: MCP)
 * 13-16: Ring (16: Tip, 15: DIP, 14: PIP, 13: MCP)
 * 17-20: Pinky (20: Tip, 19: DIP, 18: PIP, 17: MCP)
 */

export function getDistance(p1, p2) {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  const dz = (p1.z || 0) - (p2.z || 0);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function detectGesture(landmarks) {
  if (!landmarks || landmarks.length < 21) {
    return { gesture: 'NONE', cursorPoint: null };
  }

  const wrist = landmarks[0];
  const indexTip = landmarks[8];
  const middleTip = landmarks[12];
  const ringTip = landmarks[16];
  const pinkyTip = landmarks[20];

  const indexMcp = landmarks[5];
  const middleMcp = landmarks[9];
  const ringMcp = landmarks[13];
  const pinkyMcp = landmarks[17];

  // Distances from wrist (0)
  const indexDist = getDistance(indexTip, wrist);
  const middleDist = getDistance(middleTip, wrist);
  const ringDist = getDistance(ringTip, wrist);
  const pinkyDist = getDistance(pinkyTip, wrist);

  // Finger extension states relative to MCP joints
  const indexExtended = getDistance(indexTip, indexMcp) > getDistance(landmarks[6], indexMcp) * 1.25;
  const middleExtended = getDistance(middleTip, middleMcp) > getDistance(landmarks[10], middleMcp) * 1.25;
  const ringExtended = getDistance(ringTip, ringMcp) > getDistance(landmarks[14], ringMcp) * 1.25;
  const pinkyExtended = getDistance(pinkyTip, pinkyMcp) > getDistance(landmarks[18], pinkyMcp) * 1.25;

  // 1. Peace / Victory Sign (V): Index & Middle extended, Index & Middle tips > 1.30x further than Ring & Pinky
  const isPeaceRatio = indexDist > ringDist * 1.30 && middleDist > pinkyDist * 1.30;

  if (indexExtended && middleExtended && isPeaceRatio) {
    const midX = (indexTip.x + middleTip.x) / 2;
    const midY = (indexTip.y + middleTip.y) / 2;
    return {
      gesture: 'PEACE_V',
      label: '✌️ Peace Sign (+1 Point)',
      cursorPoint: { x: midX, y: midY },
    };
  }

  // 2. Open Palm (🖐️): All 4 fingers extended -> Triggers Undo Last Point
  if (indexExtended && middleExtended && ringExtended && pinkyExtended) {
    const palX = (indexTip.x + middleTip.x + ringTip.x + pinkyTip.x) / 4;
    const palY = (indexTip.y + middleTip.y + ringTip.y + pinkyTip.y) / 4;
    return {
      gesture: 'OPEN_PALM',
      label: '🖐️ Open Palm (Undo Point)',
      cursorPoint: { x: palX, y: palY },
    };
  }

  // 3. Single Index Pointing: Only Index extended
  if (indexExtended && indexDist > middleDist * 1.25 && indexDist > ringDist * 1.25) {
    return {
      gesture: 'INDEX_POINT',
      label: '☝️ Index Point',
      cursorPoint: { x: indexTip.x, y: indexTip.y },
    };
  }

  // Fallback: Hand visible but unclassified posture
  return {
    gesture: 'OTHER_POSTURE',
    label: '✋ Hand Detected',
    cursorPoint: { x: indexTip.x, y: indexTip.y },
  };
}
